import React, { useState, useEffect } from 'react';
import { Paper } from '@mui/material';
import { Switch, FormControlLabel } from '@mui/material';
import { useSelector } from 'react-redux';
import { Tooltip, Typography, Box } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import StockChart from './charts/Stockchart_MUI';
import api from '../functions/api';
import getUserId from '../functions/getUserId';

// DAS-2903: the chart's big number is the time-weighted return (the professional
// standard — deposits can't distort it); Portfolio Status shows the simple
// "euros earned / money put in" figure. Both are correct answers to different
// questions, so the simple one is shown small underneath with an explainer.
const MEASURE_EXPLANATION = (
  <Box sx={{ p: 0.5, maxWidth: 320 }} data-testid="ytd-measure-explanation">
    <Typography variant="body2" gutterBottom>
      <b>The big number (time-weighted return)</b> is the professional standard:
      it measures how well the investments themselves performed, day by day.
      Money you deposit or withdraw cannot push it up or down.
    </Typography>
    <Typography variant="body2" gutterBottom>
      <b>The small number (return on invested money)</b> is the euros earned
      this year divided by all the money that was invested (start value plus
      deposits). Deposits are never counted as profit — they only count as
      money put in. It answers "how much richer am I?" — but a deposit
      part-way through the year weighs the percentage down even when the
      investing went well.
    </Typography>
    <Typography variant="body2">
      The two will always differ slightly; both are correct answers to
      different questions. The time-weighted number is shown large because it
      is the standard way professionals report performance.
    </Typography>
  </Box>
);

const periods = [
  { label: "1D", days: 1 },
  { label: "1W", days: 7 },
  { label: "1M", days: 30 },
  { label: "3M", days: 90 },
  { label: "1Y", days: 365 },
  { label: "5Y", days: 365 * 5 },
  { label: "YTD", days: null }
];

const sources = [
  {
    label: "Portfolio",
    code: "portfolio_performance",
    yAxisFormat: "percentage",
    seriesKey: "cumulative",
    seriesMergeKey: "value",
    mergeSeriesWithExternal: false,
    tooltipFields: [
      { label: "Performance", source: "seriesData", key: "cumulative", format: "percentage" },
      { label: "Delta", source: "seriesData", key: "delta", format: "percentage" }
    ],
    chartColor: "#4caf50",
    benchmarkTicker: "^IXIC" // Nasdaq
  },
  {
    label: "Saxo",
    code: "saxo_performance",
    yAxisFormat: "percentage",
    seriesKey: "cumulative",
    seriesMergeKey: "value",
    mergeSeriesWithExternal: true,
    tooltipFields: [
      { label: "Portfolio Value", source: "data", key: "value", format: "currency" },
      { label: "Performance", source: "seriesData", key: "cumulative", format: "percentage" },
      { label: "Delta", source: "seriesData", key: "delta", format: "percentage" }
    ],
    chartColor: "#f44336",
    benchmarkTicker: "^GSPC" // S&P 500
  },
  {
    label: "DeGiro",
    code: "degiro_performance",
    yAxisFormat: "percentage",
    seriesKey: "cumulative",
    seriesMergeKey: "value",
    mergeSeriesWithExternal: true,
    tooltipFields: [
      { label: "Portfolio Value", source: "data", key: "value", format: "currency" },
      { label: "Daily Return", source: "data", key: "simple_return", format: "percentage" },
      { label: "Performance", source: "seriesData", key: "cumulative", format: "percentage" }
    ],
    chartColor: "#2196f3",
    benchmarkTicker: "^IXIC" // Nasdaq
  },
  // DAS-1937: DeGiro Rebalance performance chart
  {
    label: "DeGiro Rebalance",
    code: "degiro_rebalance_performance",
    yAxisFormat: "percentage",
    seriesKey: "cumulative",
    seriesMergeKey: "value",
    mergeSeriesWithExternal: true,
    tooltipFields: [
      { label: "Portfolio Value", source: "data", key: "value", format: "currency" },
      { label: "Daily Return", source: "data", key: "simple_return", format: "percentage" },
      { label: "Performance", source: "seriesData", key: "cumulative", format: "percentage" }
    ],
    chartColor: "#9c27b0", // Purple to distinguish from regular DeGiro (blue)
    benchmarkTicker: "^IXIC" // Nasdaq
  },
  {
    label: "W&O",
    code: "wo_performance",
    yAxisFormat: "percentage",
    seriesKey: "cumulative",
    seriesMergeKey: "value",
    mergeSeriesWithExternal: true,
    tooltipFields: [
      { label: "Portfolio Value", source: "data", key: "value", format: "currency" },
      { label: "Performance", source: "seriesData", key: "cumulative", format: "percentage" },
      { label: "Delta", source: "seriesData", key: "delta", format: "percentage" }
    ],
    chartColor: "#ff9800",
    benchmarkTicker: "URTH" // MSCI World ETF
  }
];

function transformChartData(rawData, seriesData, period, source) {
  const now = new Date();
  const from = period.label === "YTD"
    ? new Date(now.getFullYear(), 0, 1)
    : (() => { const t = new Date(); t.setDate(t.getDate() - period.days); return t })();

  const normalizeDate = (d) => {
    const parsed = new Date(d);
    if (isNaN(parsed.getTime())) return null;
    return parsed.toISOString().split("T")[0];
  };

  const normRaw = (rawData || [])
    .map(d => ({ ...d, date: normalizeDate(d.date) }))
    .filter(d => d.date);

  const normSeries = (seriesData || [])
    .map(d => ({ ...d, date: normalizeDate(d.date) }))
    .filter(d => d.date);

  const filteredRaw = normRaw.filter(d => new Date(d.date) >= from);
  const filteredSeries = normSeries.filter(d => new Date(d.date) >= from);

  let finalSeries = filteredSeries;

  // === wo_performance ===
  if (source.code === "wo_performance") {
    const finalSeries = filteredSeries.map((d, i, arr) => {
      const prev = arr[i - 1]?.cumulative;
      const delta = prev !== undefined ? d.cumulative - prev : 0;
      return {
        date: d.date,
        cumulative: d.cumulative,
        delta
      };
    });
  
    return {
      raw: filteredRaw,
      series: finalSeries
    };
  }
  
  


  if (source.code === "degiro_performance") {
    let total = 1.0;
    finalSeries = filteredRaw.map((d) => {
      const r = typeof d.simple_return === "number" ? d.simple_return : 0;
      total *= 1 + r / 100;
      const cumulative = (total - 1) * 100;
      return { ...d, cumulative };
    });
  }
  if (source.code === "portfolio_performance") {
    const finalSeries = filteredSeries.map((d, i, arr) => {
      const prev = arr[i - 1]?.cumulative;
      return {
        date: d.date,
        cumulative: d.cumulative,
        delta: prev !== undefined ? d.cumulative - prev : 0
      };
    });
  
    return {
      raw: filteredRaw,
      series: finalSeries
    };
  }
  

  return {
    raw: filteredRaw,
    series: finalSeries
  };
}


const PerformanceChart = () => {
  const [activeSourceIndex, setActiveSourceIndex] = useState(0);
  const [chartData, setChartData] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("YTD");

  // const [selectedPeriod, setSelectedPeriod] = useState("1W");
  const userId = getUserId();
  const source = sources[activeSourceIndex];
  const [saxoValid, setSaxoValid] = useState(true);
  const [benchmarkEnabled, setBenchmarkEnabled] = useState(true);
  const [benchmarkData, setBenchmarkData] = useState(null);

  // DAS-2903: simple "euros earned / money put in" YTD figure, shown small +
  // italic under the time-weighted headline (Portfolio chart, YTD only).
  const toplinePerformance = useSelector(
    (state) => state.portfolio?.data?.portfolio_topline?.[0]?.portfolio_performance
  );
  const [measureInfoOpen, setMeasureInfoOpen] = useState(false);
  const showSimpleReturn =
    source.code === 'portfolio_performance' &&
    selectedPeriod === 'YTD' &&
    toplinePerformance != null;
  const simpleReturnSubtitle = showSimpleReturn ? (
    <Typography
      variant="caption"
      color="text.secondary"
      data-testid="ytd-total-return-secondary"
      sx={{ fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
    >
      Return on invested money: {Number(toplinePerformance).toFixed(2)}%
      <Tooltip
        title={MEASURE_EXPLANATION}
        arrow
        open={measureInfoOpen}
        onOpen={() => setMeasureInfoOpen(true)}
        onClose={() => setMeasureInfoOpen(false)}
      >
        <InfoOutlinedIcon
          data-testid="ytd-measure-info"
          fontSize="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => setMeasureInfoOpen((open) => !open)}
        />
      </Tooltip>
    </Typography>
  ) : null;

  // A bar can carry no close (holiday, or today before the market settles — DAS-2600
  // stores those as NULL). `null / base` is 0 in JS, which would have drawn a fake
  // -100% spike, and arr[0] being such a bar would have based the whole series on 1.
  // So base on the first bar that HAS a price and drop the priceless bars.
  const benchmarkBase = (benchmarkData?.data || []).find(d => d.close != null)?.close || null;
  const benchmarkBars = benchmarkBase
    ? (benchmarkData?.data || []).filter(d => d.close != null)
    : [];

  const benchmarkSeries = benchmarkBars.map((d) => ({
    date: d.date,
    cumulative: ((d.close / benchmarkBase) - 1) * 100  // in percentage
  }));

  const transformedBenchmarkSeries = benchmarkBars.map((d) => ({
    date: d.date,
    value: ((d.close / benchmarkBase) - 1) * 100
  }));
  
  useEffect(() => {
    if (!userId) return;
  
    const fetchData = async () => {
      try {
        const res = await api.get('/stockchart_mui', {
          params: {
            code: source.code,
            userid: userId,
            period: selectedPeriod || "YTD"
          }
        });
  
        if (res.data.status !== 'success') {
          console.warn("Backend returned non-success:", res.data);
          setChartData({ data: [], seriesData: [] });
          return;
        }
  
        // console.log("📊 data received from fetchData:", res);
  
        const rawData = res.data.data || [];
        const series = res.data.performance_series || [];
        const finalPerformance = typeof res.data.performance === 'number' ? res.data.performance : null;

  
        const currentPeriod = periods.find(p => p.label === selectedPeriod);
        const transformed = transformChartData(rawData, series, currentPeriod, source);
  
        setChartData({
          data: transformed.raw,
          seriesData: transformed.series
        });
  
        // ✅ Fetch benchmark if enabled
        if (
          benchmarkEnabled &&
          source.benchmarkTicker &&
          transformed.series.length > 0
        ) {
          const fromDate = transformed.series[0].date;
          const toDate = transformed.series[transformed.series.length - 1].date;
  
          try {
            const benchmarkRes = await api.put('/historical_data', {
              ticker: source.benchmarkTicker,
              date_start: fromDate,
              date_end: toDate
            });
  
            // console.log("📊 Benchmark data received:", benchmarkRes.data);
            setBenchmarkData(benchmarkRes.data);
          } catch (err) {
            console.error("Benchmark fetch failed", err);
            setBenchmarkData(null);
          }
        } else {
          setBenchmarkData(null);
        }
      } catch (err) {
        console.error("API request error:", err);
        setChartData({ data: [], seriesData: [] });
        setBenchmarkData(null);
      }
    };
  
    fetchData();
  }, [activeSourceIndex, userId, selectedPeriod, benchmarkEnabled]);
  

  useEffect(() => {
    if (!userId) return;
  
    const checkSaxo = async () => {
      try {
        const res = await api.get('/saxo_validity', {
          params: { userid: userId }
        });
        // console.log("🧪 Validity", res.data); // ✅ Here
        setSaxoValid(res.data.valid);
      } catch (err) {
        setSaxoValid(false);
      }
    };
  
    checkSaxo();
  }, [userId]);
  
  // console.log("✅ Final benchmarkSeries for chart:", benchmarkSeries);

  return (
    <>
      <StockChart
  title={`${source.label} Performance`}
  externalData={chartData.data}
  seriesData={chartData.seriesData}
  seriesKey={source.seriesKey}
  seriesMergeKey={source.seriesMergeKey}
  mergeSeriesWithExternal={source.mergeSeriesWithExternal}
  tooltipFields={source.tooltipFields}
  yAxisFormat={source.yAxisFormat}
  colorUp={source.chartColor}
  colorDown={source.chartColor}
  showToolbar={true}
  onPeriodChange={(label) => setSelectedPeriod(label)}
  comparisonSeries={benchmarkEnabled ? transformedBenchmarkSeries : null}
  comparisonKey="value"
  comparisonLabel={source.benchmarkTicker}
  comparisonColor="#607d8b"
  selectedPeriod={selectedPeriod}
  subtitle={simpleReturnSubtitle}
/>




<div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
  {sources.map((s, i) => (
    <button
      key={s.code}
      onClick={() => setActiveSourceIndex(i)}
      disabled={s.code === "saxo_performance" && !saxoValid}
      style={{
        padding: '6px 12px',
        borderRadius: 8,
        border: i === activeSourceIndex ? '2px solid #1976d2' : '1px solid #ccc',
        background: i === activeSourceIndex ? '#e3f2fd' : 'white',
        cursor: s.code === "saxo_performance" && !saxoValid ? 'not-allowed' : 'pointer',
        opacity: s.code === "saxo_performance" && !saxoValid ? 0.5 : 1
      }}
    >
      {s.label}
    </button>
  ))}

<FormControlLabel
  control={
    <Switch
      size="small"
      checked={benchmarkEnabled}
      onChange={() => setBenchmarkEnabled(prev => !prev)}
      color="primary"
    />
  }
  label={
    <span style={{
      fontSize: '0.875rem',
      fontFamily: 'inherit',
      color: 'inherit'
    }}>
      Benchmark
    </span>
  }
  sx={{ marginLeft: 2 }}
/>


</div>

    </>
  );
};

export default PerformanceChart;
